import { Injectable, inject, signal } from '@angular/core';
import { GraphQLClientService } from './graphql-client.service';
import { AnalyticsService } from './analytics.service';
import { CachePersistService } from './cache-persist.service';
import { MapPreferencesSyncService } from './map-preferences-sync.service';
import { ProfileSettingsSyncService } from './profile-settings-sync.service';
import { SwitchLandingService } from './switch-landing.service';
import { landingRoleState } from './switch-landing.util';
import { FavouriteLocationsService } from './favourite-locations.service';
import { SupportSessionService } from './support-session.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import { isOk, makeError } from '../models/api-result';
import { createApiState } from '../models/api-state';

/**
 * PlatformCustomerService — platform-mode customer switching.
 *
 * RESPONSIBILITY: list the customers the signed-in user can access
 * (`customers` query, driven server-side by their customerId + customerAccess[])
 * and switch the active workspace (`switchCustomer` mutation → a token scoped to
 * the TARGET customer's tenant). Internal/RDx users with >1 customer use this to
 * jump into a demo customer ("RICOH Workplace Demo") and exercise every app
 * end-to-end. Regular single-customer users never see a switcher.
 *
 * SIGNALS:
 *   customers — accessible customers (empty until load() resolves / on failure)
 *   loaded    — true once a load attempt has resolved (gates UI flicker)
 *
 * BACKEND CONTRACT: `customers` (query) + `switchCustomer` (mutation) on
 * rdx-platform-api. The switch is claims-based: createToken embeds
 * the target customerId and target Employee _id/roles plus a stable
 * homeEmployeeId. When no target Employee exists, roles is deliberately [] —
 * projecting home roles would be privilege escalation. switchCustomer ALSO
 * sets the auth
 * cookies (withCredentials) server-side; we additionally persist the returned
 * token to localStorage because Apollo sends `spaces_tenant`/`Authorization`
 * from there and the backend lets the header win over the cookie — a stale
 * home-tenant header would otherwise break the freshly-minted token.
 *
 * USED BY: ProfilePanelComponent ("Switch workspace" section),
 * OrgSwitcherComponent (top-left logo organisation switcher), the greenfield
 * wizard (switches into the customer it just created and resumes there) and,
 * next, the customer-access page ("Switch into").
 *
 * LANDING: a switch can succeed into a customer that holds no employee row
 * for the caller — the token then carries `roles: []` and every write 403s.
 * The API reports that on the mutation as `targetRoleState`; when it says
 * `NO_EMPLOYEE_ROW` the switch parks a landing (SwitchLandingService) before
 * the reload so the shell can say so instead of letting the person find out
 * on their first save.
 *
 * AI CONTEXT: best-effort load — a failure hides the switcher (switching is a
 * convenience, never load-bearing). switchTo hard-reloads so the whole app
 * re-resolves under the new customer. Because the Apollo cache is PERSISTED to
 * IndexedDB (CachePersistService), a hard reload alone would rehydrate the
 * previous customer's data — switchTo purges the persisted cache and clears
 * every customer-scoped localStorage key before reloading so nothing from the
 * old organisation bleeds into the new session.
 */

/**
 * localStorage keys scoped to the ACTIVE customer — stale values from the
 * previous organisation must not survive a switch. Auth keys (access-token,
 * tenant, refresh-token, customer-id) are overwritten, not cleared.
 *
 * The line (ADR-035): anything derived from a CUSTOMER's records goes;
 * device-local viewer settings stay. `im-user-preferences`, panel widths and
 * the date/time format choices are deliberately absent — the viewer is the
 * same human on the same device.
 *
 * ADD A KEY HERE when you add customer-scoped storage. This list has drifted
 * before, and always by omitting a sibling of a key already in it.
 */
const CUSTOMER_SCOPED_STORAGE_KEYS = [
  'im-selected-location', // previous org's building id
  'schedule-selected-location-ids', // schedule multi-building selection
  'im-brand-color-cache', // previous org's brand colours + logo (flash on boot)
  // Offline-first caches of ProfileSettings, which is tenant-scoped
  // server-side: these are the previous EMPLOYEE's, not the person's.
  // `dropSyncBaseline()` alone leaves them on screen until hydration lands.
  'im-vehicles',
  'ct-dietary-preferences',
] as const;

/** Longest a switch waits for the persisted cache to clear before reloading. */
export const SWITCH_PURGE_TIMEOUT_MS = 3000;

/** A customer the signed-in user can switch into. */
export interface PlatformCustomer {
  id: string;
  name: string;
  logo: string | null;
  /** Demo/test customers (isTest) are labelled distinctly in the UI. */
  isTest: boolean;
}

const CUSTOMERS_QUERY = `query customers {
  customers { _id name logo isTest }
}`;

const SWITCH_CUSTOMER_MUTATION = `mutation switchCustomer(
  $customerId: String!
  $reason: String
  $ticketRef: String
  $hours: Int
) {
  switchCustomer(customerId: $customerId, reason: $reason, ticketRef: $ticketRef, hours: $hours) {
    idToken
    refreshToken
    identityTenant
    targetRoleState
    targetRoleCount
    supportSessionExpiresAt
  }
}`;

interface CustomerNode {
  _id: string;
  name?: string | null;
  logo?: string | null;
  isTest?: boolean | null;
}

interface SwitchTokenNode {
  idToken?: string | null;
  refreshToken?: string | null;
  identityTenant?: string | null;
  /**
   * How the target customer resolved a role set for the caller. GraphQL
   * ENUM names: `RESOLVED` | `NO_EMPLOYEE_ROW`. switchCustomer-only — null
   * from every other token mutation, and read defensively because an older
   * API omits it altogether, which reads as the silent RESOLVED path.
   */
  targetRoleState?: string | null;
  /** Role assignments minted into the switched token; 0 for a missing row. */
  targetRoleCount?: number | null;
  /**
   * Set when the switch opened a time-boxed Ricoh support session (all-
   * customers access into a customer where the operator has no seat of their
   * own). ISO string on the wire.
   */
  supportSessionExpiresAt?: string | null;
}

/** Which surface triggered a switch — for attribution, never for behaviour. */
export type CustomerSwitchSource =
  | 'profile'
  | 'header'
  | 'customer-access'
  | 'greenfield'
  | 'customers';

/**
 * Why a platform operator is entering, sent with the switch. Only read by the
 * API when the switch opens a Ricoh support session; it is then shown to the
 * customer in its own activity feed.
 */
export interface SupportSessionRequest {
  /** Optional: Ricoh support staff need not give one. */
  reason?: string;
  ticketRef?: string;
  hours?: number;
}

export interface CustomerSwitchOptions {
  source: CustomerSwitchSource;
  /** App path to reload into instead of the root — e.g. a wizard that
   *  resumes inside the new customer. Internal paths only. */
  returnTo?: string;
  /** The target's name when it is not in `customers()` yet (a customer
   *  created moments ago) — used for analytics and the landing copy. */
  targetName?: string;
  /** The operator console's sign-in: why, and for how long. */
  supportSession?: SupportSessionRequest;
}

@Injectable({ providedIn: 'root' })
export class PlatformCustomerService {
  private readonly graphql = inject(GraphQLClientService);
  private readonly analytics = inject(AnalyticsService);
  private readonly cachePersist = inject(CachePersistService);
  private readonly mapPrefsSync = inject(MapPreferencesSyncService);
  private readonly profileSettingsSync = inject(ProfileSettingsSyncService);
  private readonly switchLanding = inject(SwitchLandingService);
  private readonly favouriteLocations = inject(FavouriteLocationsService);
  private readonly supportSession = inject(SupportSessionService);

  private readonly _customers = signal<PlatformCustomer[]>([]);
  readonly customers = this._customers.asReadonly();

  private readonly _loaded = signal<boolean>(false);
  readonly loaded = this._loaded.asReadonly();

  /** The last switch: why it failed, cleared when a new one starts. */
  private readonly _switch = createApiState<null>(null);
  readonly switchError = this._switch.error;

  /**
   * Forget the last switch's failure. A surface that shows it calls this
   * when it opens, so a failure from somewhere else (the org switcher, a
   * support-session return) is not shown later as if it were new.
   */
  clearSwitchError(): void {
    this._switch.clearError();
  }

  private loadInFlight: Promise<void> | null = null;

  /** Fetch the accessible-customers list once (single-flight, best-effort). */
  load(): Promise<void> {
    this.loadInFlight ??= this.doLoad().finally(() => {
      this.loadInFlight = null;
    });
    return this.loadInFlight;
  }

  private async doLoad(): Promise<void> {
    const res = await this.graphql.queryResult<CustomerNode[]>(
      CUSTOMERS_QUERY,
      undefined,
      'network-only',
    );
    if (isOk(res) && res.data) {
      this._customers.set(
        res.data
          .filter((c): c is CustomerNode => !!c?._id)
          .map((c) => ({
            id: c._id,
            name: c.name?.trim() || 'Unnamed customer',
            logo: c.logo?.trim() || null,
            isTest: c.isTest === true,
          })),
      );
    } else {
      this._customers.set([]); // older API / auth not ready — hide the switcher
    }
    this._loaded.set(true);
  }

  /** The customer id the session is currently scoped to (login/switch hint). */
  currentCustomerId(): string | null {
    try {
      return localStorage.getItem('customer-id');
    } catch {
      return null;
    }
  }

  /**
   * Switch into `customerId`: mint a tenant-scoped token, persist it (cookies
   * are set server-side; we mirror to localStorage so the Apollo headers stay
   * consistent), scrub every trace of the previous customer (persisted Apollo
   * cache, customer-scoped localStorage, synced map prefs), fire analytics,
   * then hard-reload. Returns false if the mutation yielded no token (caller
   * can surface a message). `source` attributes which surface triggered the
   * switch; the options form adds `returnTo` (reload into a path other than
   * the root) and `targetName` (for a customer not yet in the list).
   */
  async switchTo(customerId: string, source?: CustomerSwitchSource): Promise<boolean>;
  async switchTo(customerId: string, options: CustomerSwitchOptions): Promise<boolean>;
  async switchTo(
    customerId: string,
    sourceOrOptions: CustomerSwitchSource | CustomerSwitchOptions = 'profile',
  ): Promise<boolean> {
    const options: CustomerSwitchOptions =
      typeof sourceOrOptions === 'string' ? { source: sourceOrOptions } : sourceOrOptions;
    const target = this._customers().find((c) => c.id === customerId);
    const targetName = target?.name ?? options.targetName;
    const from = this.currentCustomerId();

    this._switch.setLoading();
    const res = await this.graphql.mutateResult<SwitchTokenNode>(SWITCH_CUSTOMER_MUTATION, {
      customerId,
      reason: options.supportSession?.reason ?? null,
      ticketRef: options.supportSession?.ticketRef || null,
      hours: options.supportSession?.hours ?? null,
    });
    if (!isOk(res)) {
      this._switch.setError(res.error);
      return false;
    }
    if (!res.data?.idToken) {
      this._switch.setError(
        makeError('UNKNOWN', 'The switch did not return a session. Try again.'),
      );
      return false;
    }
    this._switch.setData(null);
    const token = res.data;

    // From here the new session is minted, so the only right ending is the
    // reload into it. Nothing below may strand the caller's spinner: a
    // throw is logged and the reload still happens.
    try {
      await this.prepareForReload(customerId, targetName, from, options, token);
    } catch (err) {
      console.warn('[PlatformCustomerService] Pre-switch cleanup failed; reloading anyway:', err);
    }
    this.reloadApp(options.returnTo ?? '/');
    return true;
  }

  /** Everything between minting the new session and reloading into it. */
  private async prepareForReload(
    customerId: string,
    targetName: string | undefined,
    from: string | null,
    options: CustomerSwitchOptions,
    token: SwitchTokenNode,
  ): Promise<void> {

    try {
      localStorage.setItem('access-token', token.idToken as string);
      if (token.identityTenant) localStorage.setItem('tenant', token.identityTenant);
      if (token.refreshToken) localStorage.setItem('refresh-token', token.refreshToken);
      localStorage.setItem('customer-id', customerId);
    } catch {
      /* storage unavailable — the withCredentials cookies still carry the switch */
    }

    // Drop the previous customer's SYNC BASELINES so boot re-hydrates for the
    // new account instead of migrating the old account's values up.
    //
    // `dropSyncBaseline()`, NOT `reset()`. `reset()` is the logout path: it
    // resets UserPreferences to DEFAULTS — `onboardingComplete: false` among
    // them — so switching customer re-ran the first-run tour every time and
    // discarded the viewer's theme, lighting and accessibility choices. The
    // viewer is the same person on the same device; only the synced baseline
    // is customer-scoped.
    //
    // ProfileSettings is dropped for the same reason it is synced at all: it
    // is per-employee, so the previous customer's baseline must not be the
    // thing the next customer's values are diffed against.
    try {
      this.mapPrefsSync.dropSyncBaseline();
      this.profileSettingsSync.dropSyncBaseline();
    } catch {
      /* prefs reset is best-effort */
    }

    // Favourite buildings are customer-scoped — the ids only mean anything
    // inside the tenant they were starred in, so they must not survive a
    // switch. Boot re-hydrates them for the new account.
    try {
      this.favouriteLocations.reset();
    } catch {
      /* favourites reset is best-effort */
    }

    // Clear customer-scoped localStorage so the new session doesn't boot into
    // the previous org's building / schedule selection / brand colours.
    try {
      for (const key of CUSTOMER_SCOPED_STORAGE_KEYS) {
        localStorage.removeItem(key);
      }
    } catch {
      /* storage unavailable — nothing to clear */
    }

    // CRITICAL: the Apollo cache is persisted to IndexedDB, so a hard reload
    // would otherwise rehydrate the OLD customer's data (bookings, people,
    // articles) into the new session. Purge before reloading.
    //
    // Bounded: purge is an IndexedDB call with no timeout of its own, and a
    // blocked database (another tab mid-upgrade) would otherwise leave the
    // switch spinning for ever. The boot hydrate is bounded the same way.
    await Promise.race([
      this.cachePersist.purge(),
      new Promise<void>((resolve) => setTimeout(resolve, SWITCH_PURGE_TIMEOUT_MS)),
    ]);

    this.analytics.capture(ANALYTICS_EVENTS.CUSTOMER_SWITCHED, {
      target_customer_id: customerId,
      target_customer_name: targetName ?? 'unknown',
      from_customer_id: from ?? undefined,
      source: options.source,
    });

    // The target holds no employee row for this person, or a row with no
    // roles: the switch "worked" but nothing there will save. Park the
    // verdict for the shell that the reload boots — sessionStorage, so it
    // survives the sweep above.
    const roleState = landingRoleState(token.targetRoleState, token.targetRoleCount);
    if (roleState) {
      this.switchLanding.park({
        customerId,
        customerName: targetName ?? '',
        roleState,
        roleCount: token.targetRoleCount ?? 0,
        at: Date.now(),
      });
    }

    // A Ricoh support session: park what the banner needs on the far side of
    // the reload. Written AFTER the customer-scoped sweep above, under a key
    // it never touches. Going home (no session opened) clears it.
    if (token.supportSessionExpiresAt) {
      this.supportSession.recordStarted({
        customerId,
        customerName: targetName ?? '',
        expiresAt: token.supportSessionExpiresAt,
      });
    } else if (customerId === this.supportSession.homeCustomerId()) {
      this.supportSession.recordReturnedHome();
    }
  }

  /** Hard reload into `path` (the app root by default) — overridable in
   *  tests (jsdom has no navigation). */
  protected reloadApp(path = '/'): void {
    window.location.assign(path);
  }
}
